/**
 * Page implementation the change of the password for the connected user.
 *
 * Copyright © Ircam Amplify 2024-2026
 */

import { AppRouteUrl } from '@/AppRoute'
import { RhfFormInputPassword } from '@/components/RhfFormInput'
import '@/pages/Login/Login.css'
import LogInButton from '@/pages/Login/LoginButton'
import LoginDialog from '@/pages/Login/LoginDialog'
import { zodResolver } from '@hookform/resolvers/zod'
import { updatePassword } from 'aws-amplify/auth'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z as zod } from 'zod'

/**
 * The Zod schema defining the fields and their data type.
 */
const ChangePasswordSchema = zod.object({
  oldPassword: zod.string().trim().min(1, 'Required'),
  newPassword: zod.string().trim().min(1, 'Required'),
  confirmedPassword: zod.string().trim().min(1, 'Required'),
})
type ChangePasswordType = zod.infer<typeof ChangePasswordSchema>

export const ChangePasswordPage = () => {
  const navigate = useNavigate()

  // Get the needed functions defined by react-use-form
  // const formFunctionList
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ChangePasswordType>({
    mode: 'all',
    resolver: zodResolver(ChangePasswordSchema),
  })

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)

  const submitChangePassword = async (data: ChangePasswordType) => {
    if (data.newPassword !== data.confirmedPassword) {
      setLoading(false)
      setErrorMsg('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      await updatePassword({
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      })
      navigate(AppRouteUrl.Home)
    } catch (e: any) {
      if (e.name === 'NotAuthorizedException') {
        setErrorMsg('Your old password is invalid')
      } else {
        setErrorMsg(e.message)
      }
      setLoading(false)
    }
  }
  const changePassword = handleSubmit(submitChangePassword)

  function closeErrorDialog() {
    setErrorMsg(undefined)
  }
  return (
    <>
      <div className="login-page">
        <h1 className="login-title">Change Your Password</h1>

        <form onSubmit={changePassword} className="rhf-form">
          <RhfFormInputPassword
            title="Old Password"
            id="oldPassword"
            register={register}
            error={errors.oldPassword}
            control={control}
          />

          <RhfFormInputPassword
            title="New Password"
            id="newPassword"
            register={register}
            error={errors.newPassword}
            control={control}
          />

          <RhfFormInputPassword
            title="Confirmed Password"
            id="confirmedPassword"
            register={register}
            error={errors.confirmedPassword}
            control={control}
          />

          <div className="login-submit-container">
            <LogInButton
              text="Change Password"
              type="submit"
              disabled={loading || !isValid}
            />

            <LogInButton
              text="Cancel"
              toLink={AppRouteUrl.Home}
              disabled={loading || !isValid}
              dark={false}
            />
          </div>
        </form>
      </div>

      <LoginDialog
        title={'Password Error'}
        message={errorMsg}
        onClick={closeErrorDialog}
        onClose={closeErrorDialog}
        open={errorMsg !== undefined}
      />
    </>
  )
}
